function createRootKey(key: string) {
    return () => [key] as const;
}

function createQueryKeys(key: string) {
    const all = createRootKey(key);

    const lists = () => [...all(), "list"] as const;
    const list = (filters: object = {}) => [...lists(), filters] as const;

    const pagedLists = () => [...all(), "pagedList"] as const;
    const pagedList = (filters: object = {}) => [...pagedLists(), filters] as const;

    const details = () => [...all(), "detail"] as const;
    const detail = (id: string | number) => [...details(), id] as const;

    return { all, lists, list, pagedLists, pagedList, details, detail };
}

export const queryKeys = {
    accounts: {
        ...createQueryKeys("accounts"),
        getByPhone: (phone: string) => [...queryKeys.accounts.all(), "getByPhone", phone] as const,
        getByIds: (ids: (string | number)[] = []) => [...queryKeys.accounts.all(), "getByIds", ids] as const,
        feesAll: () => [...queryKeys.accounts.all(), "fees"] as const,
        fees: (accountId: number) => [...queryKeys.accounts.feesAll(), accountId] as const,
        balances: () => [...queryKeys.accounts.all(), "balances"] as const,
        balance: (accountId: number) => [...queryKeys.accounts.balances(), accountId] as const,
    },
    accountCreditEntries: createQueryKeys("accountCreditEntries"),
    accountFees: createQueryKeys("accountFees"),
    accountPhoneNumbers: createQueryKeys("accountPhoneNumbers"),
    accountTypes: createQueryKeys("accountTypes"),
    accountUnits: createQueryKeys("accountUnits"),
    appSettings: {
        all: createRootKey("appSettings"),
        me: () => [...queryKeys.appSettings.all(), "me"] as const,
        tenant: (tenantId: number) => [...queryKeys.appSettings.all(), tenantId] as const,
    },
    auditLogs: createQueryKeys("auditLogs"),
    auditLogCategories: createQueryKeys("auditLogCategories"),
    awsFiles: {
        all: createRootKey("awsFiles"),
    },
    callBacks: createQueryKeys("callBacks"),
    cases: {
        ...createQueryKeys("cases"),
        detailDeep: (caseId: number) => [caseId, "detailDeep"] as const,
        detailsDeep: () => [...queryKeys.cases.all(), "detailsDeep"] as const,
        forms: (caseId: number) => [caseId, "forms"] as const,
        productCode: (caseId: number) => [caseId, "productCode"] as const,
    },
    caseFiles: createQueryKeys("caseFiles"),
    caseFileCategories: createQueryKeys("caseFileCategories"),
    caseFollowUps: createQueryKeys("caseFollowUps"),
    caseNote: createQueryKeys("caseNote"),
    claims: createQueryKeys("claims"),
    countries: createQueryKeys("countries"),
    dashboards: {
        admin: createRootKey("adminDashboard"),
        recovery: (filters: object = {}) => ["recoveryDashboard", filters] as const,
        lab: (filters: object = {}) => ["labDashboard", filters] as const,
        processing: (filters: object = {}) => ["processingDashboard", filters] as const,
        distribution: (filters: object = {}) => ["distributionDashboard", filters] as const,
        shipment: (filters: object = {}) => ["shipmentDashboard", filters] as const,
        mcr: (filters: object = {}) => ["mcrDashboard", filters] as const,
        qa: (filters: object = {}) => ["qaDashboard", filters] as const,
        invoicing: (filters: object = {}) => ["invoicingDashboard", filters] as const,
        surgery: (filters: object = {}) => ["surgeryDashboard", filters] as const,
    },
    determinations: createQueryKeys("determinations"),
    donors: {
        all: createRootKey("donors"),
        chartReviewLogs: (filters: object = {}) => [...queryKeys.donors.all(), "chartReviewLogs", filters] as const,
    },
    donorLetters: {
        all: createRootKey("donorLetters"),
    },
    feeTypes: createQueryKeys("feeTypes"),
    followUps: createQueryKeys("followUps"),
    forms: createQueryKeys("forms"),
    formCategories: createQueryKeys("formCategories"),
    formData: createQueryKeys("formData"),
    formDataArchives: createQueryKeys("formDataArchives"),
    formSelects: createQueryKeys("formSelects"),
    formSelectOptions: createQueryKeys("formSelectOptions"),
    formSelectCategories: createQueryKeys("formSelectCategories"),
    formTypes: createQueryKeys("formTypes"),
    invoices: {
        ...createQueryKeys("invoices"),
        flagged: () => [...queryKeys.invoices.all(), "flagged"] as const,
        unpaid: () => [...queryKeys.invoices.all(), "unpaid"] as const,
        lines: (filters: object = {}) => [...queryKeys.invoices.all(), "lines", filters] as const,
        latestInvoiceNumber: () => [...queryKeys.invoices.all(), "latest-invoice-number"] as const,
    },
    labels: {
        all: createRootKey("labels"),
        case: (filters: object = {}) => [...queryKeys.labels.all(), "case", filters] as const,
    },
    labelTypes: createQueryKeys("labelTypes"),
    labKey: {
        auth: () => ["labkey", "auth"] as const,
        export: () => ["labkey", "export"] as const,
    },
    lists: {
        all: createRootKey("lists"),
        list: (filters: object = {}) => [...queryKeys.lists.all(), "lists", filters] as const,
    },
    notificationConfigs: createQueryKeys("notificationConfigs"),
    offices: createQueryKeys("offices"),
    orders: createQueryKeys("orders"),
    person: {
        ...createQueryKeys("person"),
        me: () => [...queryKeys.person.all(), "me"] as const,
        surgeonEmails: (filters: object = {}) => [...queryKeys.person.all(), "surgeon-emails", filters] as const,
    },
    personTypes: createQueryKeys("personTypes"),
    photos: createQueryKeys("photos"),
    procedures: {
        ...createQueryKeys("procedures"),
        postProcedureEval: (procedureId: number, tissueId: number) =>
            [...queryKeys.procedures.all(), "post-procedure-eval", procedureId, tissueId] as const,
        invoice: (procedureId: number) => [...queryKeys.procedures.all(), "invoice", procedureId] as const,
    },
    procedureTypes: createQueryKeys("procedureTypes"),
    profile: {
        all: createRootKey("profile"),
        me: () => [...queryKeys.profile.all(), "me"] as const,
        myProfile: () => [...queryKeys.profile.all(), "myProfile"] as const,
        impersonator: () => [...queryKeys.profile.all(), "impersonator"] as const,
        updatePassword: () => [...queryKeys.profile.all(), "update"] as const,
    },
    quickbooks: {
        classes: () => ["quickbooks", "classes"] as const,
        customer: (id: string) => ["quickbooks", "customer", id] as const,
        customers: () => ["quickbooks", "customers"] as const,
        invoice: (id: string) => ["quickbooks", "invoice", id] as const,
        invoices: () => ["quickbooks", "invoices"] as const,
        item: (id: string) => ["quickbooks", "item", id] as const,
        items: () => ["quickbooks", "items"] as const,
        latestInvoiceNumber: () => ["quickbooks", "latest-invoice-number"] as const,
        preferences: () => ["quickbooks", "preferences"] as const,
        term: (id: string) => ["quickbooks", "term", id] as const,
        terms: () => ["quickbooks", "terms"] as const,
        accounts: () => ["quickbooks", "accounts"] as const,
        auth: () => ["quickbooks", "auth"] as const,
        sync: () => ["quickbooks", "sync"] as const,
    },
    recoveryAppImports: createQueryKeys("recoveryAppImports"),
    referrals: {
        active: createRootKey("activeReferrals"),
    },
    regions: createQueryKeys("regions"),
    reports: createQueryKeys("reports"),
    roles: createQueryKeys("roles"),
    shipments: {
        ...createQueryKeys("shipments"),
        latestForTissue: (tissueId: number | string) => ["latestForTissue", tissueId] as const,
    },
    shippingMethods: createQueryKeys("shippingMethods"),
    shippingStatuses: createQueryKeys("shippingStatuses"),
    standardFees: createQueryKeys("standardFees"),
    surgeries: {
        ...createQueryKeys("surgeries"),
        calendarMetrics: () => [...queryKeys.surgeries.all(), "calendar-metrics"] as const,
        invoice: (surgeryId: number) => [...queryKeys.surgeries.all(), "invoice", surgeryId] as const,
    },
    surgerySchedulerSurgeons: {
        ...createQueryKeys("surgerySchedulerSurgeons"),
        mySurgeons: (filters: object = {}) =>
            [...queryKeys.surgerySchedulerSurgeons.all(), "my-surgeons", filters] as const,
    },
    surgeryStatuses: createQueryKeys("surgeryStatuses"),
    surgeryTypes: createQueryKeys("surgeryTypes"),
    tenants: createQueryKeys("tenants"),
    territories: createQueryKeys("territories"),
    tissueOffers: createQueryKeys("tissueOffers"),
    tissueOfferStatuses: createQueryKeys("tissueOfferStatuses"),
    tissues: {
        ...createQueryKeys("tissues"),
        clearedTissues: (filters: object = {}) => [...queryKeys.tissues.all(), "cleared-tissues", filters] as const,
        placement: (tissueId: number) => [...queryKeys.tissues.all(), "placement", tissueId] as const,
        initialEval: (tissueId: number) => [...queryKeys.tissues.all(), "initial-eval", tissueId] as const,
        summary: (tissueId: number) => [...queryKeys.tissues.all(), "summary", tissueId] as const,
        invoices: () => [...queryKeys.tissues.all(), "invoices"] as const,
        invoice: (tissueId: number) => [...queryKeys.tissues.invoices(), "invoice", tissueId] as const,
        productCode: (tissueId: number) => [...queryKeys.tissues.all(), "product-code", tissueId] as const,
    },
    tissueReports: createQueryKeys("tissueReports"),
    tissueStorage: createQueryKeys("tissueStorage"),
    tissueTypes: createQueryKeys("tissueTypes"),
    users: {
        ...createQueryKeys("users"),
        detailPerson: (userId: number) => [...queryKeys.users.details(), userId, "person"] as const,
    },
    usersBasic: createQueryKeys("usersBasic"),
};
